import { useApi } from "@backstage/core-plugin-api";
import { opsgenieApiRef } from "../api";
import { useCallback, useEffect } from "react";
import { useAsyncFn } from "react-use";

export interface Props {
  teamName?: string;
  fromDaysInPast?: number;
}

export const useIncidents = ({ teamName, fromDaysInPast = 7 }: Props) => {
  const opsgenie = useApi(opsgenieApiRef);

  const getIncidents = useCallback(async () => {
    const incidents = await opsgenie.getIncidents({ fromDaysInPast });

    if (!teamName) {
      return incidents;
    }

    // Step 1: Resolve async logic first
    const incidentsWithResolvedTeams = await Promise.all(
      incidents.map(async (incident) => {
        const respondersWithResolvedTeams = await Promise.all(
          incident.responders.map(async (r) => {
            if (r.type === "team") {
              return { ...r, name: await opsgenie.getTeamNameById(r.id) };
            }
            return r; // For "user" type, we return the same responder
          })
        );
        return { ...incident, responders: respondersWithResolvedTeams };
      })
    );

    // Step 2: Filter synchronously
    return incidentsWithResolvedTeams.filter((incident) => {
      return incident.responders.some((r: any) => {
        if (r.type === "team") {
          return r.name === teamName;
        }
        return r.teams ? r.teams.includes(teamName) : false;
      });
    });
  }, [opsgenie, teamName, fromDaysInPast]);

  const [state, fetchIncidents] = useAsyncFn(async () => {
    return await getIncidents();
  }, []);

  useEffect(() => {
    fetchIncidents();
  }, [fetchIncidents]);

  return {
    loading: state.loading,
    error: state.error,
    results: state?.value,
    refetch: fetchIncidents,
  };
};
